import { Link } from "react-router-dom";
import { ArrowRight, MessageCircleHeart } from "lucide-react";
import { Button } from "@groot/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { listRowClass } from "@groot/ui/list-row";
import { SkeletonList } from "@groot/ui/loading-skeleton";
import { Text } from "@groot/ui/typography";
import { cn } from "@groot/ui/lib/utils";
import { formatRelativeTime } from "@groot/shell/lib/date.utils";
import { usePrintBatches, type PrintBatchSourceSummary } from "../hooks/usePrintBatches";

interface Reply {
  batchId: number;
  source: PrintBatchSourceSummary & { preview: NonNullable<PrintBatchSourceSummary["preview"]> };
}

/**
 * Dashboard card: customers who have swiped through their print preview,
 * change requests first, then the most recent. Only batches not yet printed,
 * since replies on printed ones need no action.
 */
export function CustomerReplies() {
  const { data: batches, isLoading } = usePrintBatches();

  const replies: Reply[] = (batches ?? [])
    .filter((batch) => batch.status === "draft")
    .flatMap((batch) =>
      batch.sources
        .filter(
          (source): source is Reply["source"] =>
            !!source?.preview && source.preview.loved + source.preview.changes > 0,
        )
        .map((source) => ({ batchId: batch.id, source })),
    )
    .sort((a, b) => {
      const changes = Number(b.source.preview.changes > 0) - Number(a.source.preview.changes > 0);
      if (changes !== 0) return changes;
      return (b.source.preview.lastRespondedAt ?? "").localeCompare(
        a.source.preview.lastRespondedAt ?? "",
      );
    });

  if (!isLoading && (batches?.length ?? 0) === 0) return null;

  return (
    <Card className="pb-0">
      <CardHeader>
        <CardTitle>Customer replies</CardTitle>
        <CardAction>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/print-batches">
              Print <ArrowRight className="size-4" />
            </Link>
          </Button>
        </CardAction>
      </CardHeader>
      {isLoading ? (
        <SkeletonList rows={3} leading="none" secondaryLine className="border-t border-border" />
      ) : replies.length === 0 ? (
        <CardContent className="flex items-center gap-2 border-t border-border py-4">
          <MessageCircleHeart className="size-4 text-muted-foreground" />
          <Text variant="meta">
            No replies yet. Customers' swipes on shared previews show up here.
          </Text>
        </CardContent>
      ) : (
        <ul className="divide-y divide-border border-t border-border">
          {replies.map(({ batchId, source }) => {
            const { loved, changes, pending, lastRespondedAt } = source.preview;
            return (
              <li key={`${batchId}-${source.photoRequestId}`}>
                <Link
                  to={`/print-batches/${batchId}`}
                  className={cn(listRowClass, "items-start justify-between sm:items-center")}
                >
                  <div className="min-w-0 space-y-1">
                    <Text className="block truncate font-medium">{source.customerName}</Text>
                    <Text variant="meta" className="block truncate">
                      {source.orderNumber}
                      {lastRespondedAt && ` · ${formatRelativeTime(lastRespondedAt)}`}
                      {source.previewSubmittedAt
                        ? " · sent"
                        : pending > 0
                          ? ` · ${pending} to review`
                          : ""}
                    </Text>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {changes > 0 && (
                      <span className="rounded-md bg-warning/15 px-1.5 py-0.5 text-xs text-warning">
                        ✎ {changes} change{changes === 1 ? "" : "s"}
                      </span>
                    )}
                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      ♥ {loved}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
